/**
 * The explorer's view of the API.
 *
 * **Money arrives as strings and stays strings.** Every figure has been kept
 * exact through a parser, a decimal library, an integer artifact and a query
 * layer; parsing it to a JS number here to print it would undo all of that at
 * the very last step. `formatMoney` groups digits without ever converting.
 *
 * The one place a figure legitimately becomes a number is chart geometry —
 * turning a value into a pixel position. That is not arithmetic on money, and
 * the result is never displayed.
 */

export interface PeriodSummary {
  period: string;
  label: string;
  fyMonth: number;
  activeStaff: number;
  staffInDeficit: number;
  everInDeficitThisYear: number;
  crossedIntoDeficit: number;
  crossedOutOfDeficit: number;
}

export interface CampusSummary {
  campusId: string;
  campus: string;
  sheet: string;
  region: string;
  activeStaff: number;
  staffInDeficit: number;
  everInDeficitThisYear: number;
  crossedIntoDeficit: number;
  crossedOutOfDeficit: number;
  overallPosition: string;
  ytdIncome: string;
  ytdExpense: string;
}

export interface Row {
  rowId: string;
  jobCode: string;
  recipient: string;
  campus: string;
  sheet: string;
  region: string;
  categoryAndDays: string;
  broughtForward: string;
  ytdIncome: string;
  ytdExpense: string;
  ytdSurplus: string;
  overallPosition: string;
  annualSrt?: string;
  monthsOfEquity?: string;
  inDeficitNow: boolean;
  everInDeficitThisYear: boolean;
  crossedIntoDeficit: boolean;
  crossedOutOfDeficit: boolean;
  monthsInDeficit: number;
  apportioned: boolean;
  isStaff: boolean;
}

export interface MonthFigure {
  fyMonth: number;
  income: string;
  expense: string;
  surplus: string;
  position?: string;
}

export interface Change {
  jobCode: string;
  recipient: string;
  campus: string;
  fromSurplus: string;
  toSurplus: string;
  change: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin" });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(body.error ?? response.statusText, response.status);
  }
  return (await response.json()) as T;
}

export const api = {
  async signIn(password: string): Promise<void> {
    const response = await fetch("/api/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password }),
      credentials: "same-origin",
    });
    if (!response.ok) throw new ApiError("Incorrect password", response.status);
  },
  async signOut(): Promise<void> {
    await fetch("/api/logout", { method: "POST", credentials: "same-origin" });
  },
  session: () => get<{ signedIn: boolean }>("/api/session"),
  periods: () => get<{ periods: PeriodSummary[] }>("/api/periods"),
  campuses: (period: string) =>
    get<{ period: string; campuses: CampusSummary[] }>(`/api/campuses?period=${period}`),
  campus: (period: string, sheet: string) =>
    get<{
      period: string;
      campus: CampusSummary;
      rows: (Row & { months: MonthFigure[] })[];
      inDeficit: Row[];
      unclassifiedInDeficit: Row[];
    }>(`/api/campus?period=${period}&sheet=${encodeURIComponent(sheet)}`),
  job: (period: string, code: string) =>
    get<{ period: string; rows: (Row & { months: MonthFigure[] })[] }>(
      `/api/job?period=${period}&code=${encodeURIComponent(code)}`,
    ),
  deficit: (period: string) =>
    get<{ period: string; staff: Row[]; unclassified: Row[] }>(`/api/deficit?period=${period}`),
  compare: (period: string, from: number, to: number) =>
    get<{ period: string; compared: number; fell: Change[]; rose: Change[] }>(
      `/api/compare?period=${period}&from=${from}&to=${to}`,
    ),
};

/**
 * Group the digits of a decimal string. Never parses to a number: `"-9215.62"`
 * becomes `"-9,215.62"` by string surgery alone.
 */
export function formatMoney(value: string | undefined): string {
  if (value === undefined) return "—";
  const negative = value.startsWith("-");
  const [whole = "0", fraction = "00"] = value.replace("-", "").split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "−" : ""}$${grouped}.${fraction}`;
}

/** October is 1. */
export const FY_MONTH_NAMES = [
  "Oct", "Nov", "Dec", "Jan", "Feb", "Mar",
  "Apr", "May", "Jun", "Jul", "Aug", "Sep",
];
